/**
 * Module dependencies.
 */

import { Button, ButtonProps } from 'src/components/core/buttons/button';
import { ElementType, ReactNode, useCallback } from 'react';
import { Heading } from 'src/components/core/heading';
import { RawHtml } from '@untile/react-core/components/raw-html';
import { Text } from 'src/components/core/text';
import { formatPhoneNumber } from 'src/core/utils/numbers';
import { media } from '@untile/react-core/styles/media';
import { motion } from 'framer-motion';
import { routes } from 'src/core/constants/routes';
import { useRouter } from 'next/router';
import { useSchedule } from 'src/providers/schedule';
import { useSettings } from 'src/providers/settings';
import { useTranslation } from 'next-i18next';
import alertSvg from 'src/assets/svgs/alert-octagon.svg';
import checkSvg from 'src/assets/svgs/check.svg';
import retrySvg from 'src/assets/svgs/retry.svg';
import styled from 'styled-components';
import successSvg from 'src/assets/svgs/confetti.svg';

/**
 * `Props` type.
 */

type Props = {
  action: Omit<ButtonProps, 'children'> & { label: string };
  as?: ElementType;
  children?: ReactNode;
  className?: string;
  description: string;
  title: string;
  type: 'error' | 'success' | 'unavailable' | 'paymentUnavailable';
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled(motion.div)`
  color: var(--color-cyan50);
  max-width: 55%;

  ${media.max.md`
    max-width: 100%;
    padding: 0 var(--space-sm);
  `}
`;

/**
 * `StyledButton` styled component.
 */

const StyledButton = styled(Button)`
  min-width: 150px;

  ${media.max.sm`
    width: 100%;

    > span {
      justify-content: center;
      width: 100%;
    }
  `}
`;

/**
 * `Details` styled component.
 */

const Details = styled(Text).attrs({ as: 'p', variant: 'bodyXl' })`
  color: var(--color-slate300);
  margin-bottom: var(--space-xxs);

  a {
    font-weight: 600;
    text-decoration: underline;
    white-space: nowrap;
  }

  span {
    color: var(--color-slate300);
    margin-left: 2px;
  }
`;

/**
 * `Message` component.
 */

const Message = (props: Props) => {
  const { action, children, description, title, type, ...rest } = props;
  const { label, ...actionProps } = action;

  return (
    <Wrapper {...rest} animate={{ opacity: 1 }} exit={{ opacity: 0 }} initial={{ opacity: 0 }}>
      <Heading
        asDescription={'p'}
        asTitle={'h1'}
        description={description}
        descriptionTheme={'slate300'}
        icon={type === 'success' ? successSvg : alertSvg}
        isDescriptionRaw
        style={{ marginBottom: 'var(--space-sm)' }}
        title={title}
        titleSize={28}
      />

      {children}

      {type !== 'success' && (
        <StyledButton {...actionProps} colorTheme={'ghost'} size={'medium'}>
          {label}
        </StyledButton>
      )}
    </Wrapper>
  );
};

/**
 * Export `SuccessMessage` component.
 */

export const SuccessMessage = ({ children }: Pick<Props, 'children'>) => {
  const { t } = useTranslation('schedule');
  const { isSantanderFlow, processCode, reset } = useSchedule();

  const router = useRouter();
  const titleKey = isSantanderFlow ? 'messages.success.santander.title' : 'messages.success.title';
  const descriptionKey = isSantanderFlow ? 'messages.success.santander.description' : 'messages.success.description';

  return (
    <Message
      action={{
        icon: checkSvg,
        label: t('common:actions.agree'),
        onClick: () => {
          router.push(isSantanderFlow ? routes.landingPage : '/').then(reset);
        },
        reverse: true
      }}
      description={t(descriptionKey)}
      title={t(titleKey)}
      type={'success'}
    >
      {children}

      <Text
        as={'p'}
        style={{
          marginTop: 'var(--space-ms)'
        }}
        variant={'bodyM'}
      >
        <RawHtml>{t(`messages.success.codeLabel`, { code: processCode })}</RawHtml>
      </Text>

      <Text
        as={'p'}
        style={{
          color: 'var(--color-slate300)',
          marginTop: 'var(--space-xs)'
        }}
        variant={'bodyM'}
      >
        {t(`messages.success.description2`)}
      </Text>
    </Message>
  );
};

/**
 * Export `ErrorMessage` component.
 */

export const ErrorMessage = ({ type }: { type: 'error' | 'unavailable' | 'paymentUnavailable' }) => {
  const { t } = useTranslation('schedule');
  const { isSantanderFlow, reset } = useSchedule();
  const router = useRouter();
  const settings = useSettings();

  const handleClick = useCallback(() => {
    if (type === 'error') {
      reset();

      return;
    }

    router.push(isSantanderFlow ? routes.landingPage : '/').then(reset);
  }, [isSantanderFlow, reset, router, type]);

  return (
    <Message
      action={{
        ...(type === 'error' && { icon: retrySvg, reverse: true }),
        label: t(type === 'error' ? 'messages.error.action' : 'common:actions.close'),
        onClick: handleClick
      }}
      description={t(`messages.${type}.description`)}
      title={t(`messages.${type}.title`)}
      type={type}
    >
      <Text
        as={'p'}
        style={{
          color: 'var(--color-slate300)',
          marginBottom: 'var(--space-xs)',
          paddingTop: 'var(--space-ms)'
        }}
        variant={'bodyXl'}
        weight={600}
      >
        {t('messages.error.detailsLabel')}
      </Text>

      <RawHtml element={Details}>
        {t('messages.error.details', {
          formattedPhone: formatPhoneNumber(settings?.phone_number as string),
          phone: settings?.phone_number
        })}
      </RawHtml>

      <Text
        as={'div'}
        style={{
          color: 'var(--color-slate300)',
          marginBottom: 48
        }}
        variant={'bodyXs'}
        weight={600}
      >
        {t('messages.error.phone')}
      </Text>
    </Message>
  );
};
